import {IThemeConfig, ThemeColorsEnum} from './theme.entity';
import {adjustLightness} from '../utils/color.util';

export class Theme {
  private readonly primary: string;
  private readonly primary80: string;
  private readonly primary60: string;
  private readonly primary20: string;
  private readonly onPrimary: string;
  private readonly surface: string;
  private readonly onSurface: string;
  private readonly surfaceAvatar: string;
  private readonly onSurfaceAvatar: string;
  private readonly surfaceVariant: string;
  private readonly selectedGroup: string;
  private readonly selectedItem: string;
  private readonly selectedItemHover: string;
  private readonly onSelectedItem: string;
  private readonly surfaceContainer: string;
  private readonly surfaceLogo: string;
  private readonly organizationLogo?: string;
  private readonly bannerDesignType?: string;
  private readonly bannerImageUrl?: string;
  private readonly bannerImageMobileUrl?: string;

  constructor(colors: IThemeConfig) {
    this.primary = colors.primary;
    this.primary80 = colors.primary80;
    this.primary60 = colors.primary60;
    this.primary20 = colors.primary20;
    this.onPrimary = colors.onPrimary;
    this.surface = colors.surface;
    this.onSurface = colors.onSurface;
    this.surfaceAvatar = colors.surfaceAvatar;
    this.onSurfaceAvatar = colors.onSurfaceAvatar;
    this.surfaceVariant = colors.surfaceVariant;
    this.selectedGroup = colors.selectedGroup;
    this.selectedItem = colors.selectedItem;
    this.selectedItemHover = colors.selectedItemHover;
    this.onSelectedItem = colors.onSelectedItem;
    this.surfaceContainer = colors.surfaceContainer;
    this.surfaceLogo = colors.surfaceLogo;
    this.organizationLogo = colors.organizationLogo;
    this.bannerDesignType = colors.bannerDesignType;
    this.bannerImageUrl = colors.bannerImageUrl;
    this.bannerImageMobileUrl = colors.bannerImageMobileUrl;
  }

  public get themeProperties() {
    return {
      '--color-primary': this.primary,
      '--color-primary80': this.primary80,
      '--color-primary60': this.primary60,
      '--color-primary20': this.primary20,
      '--color-on-primary': this.onPrimary,
      '--color-surface': this.surface,
      '--color-on-surface': this.onSurface,
      '--color-surface-avatar': this.surfaceAvatar,
      '--color-on-surface-avatar': this.onSurfaceAvatar,
      '--color-surface-variant': this.surfaceVariant,
      '--color-selected-group': this.selectedGroup,
      '--color-selected-item': this.selectedItem,
      '--color-selected-item-hover': this.selectedItemHover,
      '--color-on-selected-item': this.onSelectedItem,
      '--color-surface-container': this.surfaceContainer,
      '--color-surface-logo': this.surfaceLogo,
      '--organization-logo': this.organizationLogo || '',
      '--banner-design-type': this.bannerDesignType || '',
      '--banner-image-url': this.bannerImageUrl ? `url("${this.bannerImageUrl}")` : '',
      '--banner-image-mobile-url': this.bannerImageMobileUrl ? `url("${this.bannerImageMobileUrl}")` : ''
    };
  }

  public static applyTheme(theme: Theme, document: Document) {
    const properties = theme.themeProperties;
    for (const [key, value] of Object.entries(properties)) {
      document.documentElement.style.setProperty(key, value);
    }
  }
}

const defaultThemeConfig: IThemeConfig = {
  primary: ThemeColorsEnum.DEFAULT,
  primary80: adjustLightness(ThemeColorsEnum.DEFAULT, -25),
  primary60: adjustLightness(ThemeColorsEnum.DEFAULT, -15),
  primary20: adjustLightness(ThemeColorsEnum.DEFAULT, 40),
  onPrimary: '#FFFFFF',
  surface: '#D5E9F7',
  onSurface: '#1C1E22',
  surfaceAvatar: '#E9F6FF',
  onSurfaceAvatar: '#00538F',
  surfaceVariant: adjustLightness(ThemeColorsEnum.DEFAULT, -12),
  selectedGroup: adjustLightness(ThemeColorsEnum.DEFAULT, 49),
  selectedItem: adjustLightness(ThemeColorsEnum.DEFAULT, 41),
  selectedItemHover: adjustLightness(ThemeColorsEnum.DEFAULT, 45),
  onSelectedItem: adjustLightness(ThemeColorsEnum.DEFAULT, -18),
  surfaceContainer: adjustLightness(ThemeColorsEnum.DEFAULT, 46),
  surfaceLogo: adjustLightness(ThemeColorsEnum.DEFAULT, 41)
};

const defaultTheme = new Theme(defaultThemeConfig);

export const themes = {
  default: defaultTheme
};
